import React, { useEffect, useState } from 'react';
import { Box, CardContent, Typography, TextField, InputAdornment, FormControlLabel, Switch, Button, Autocomplete, Chip } from '@mui/material';
import { Build, Search, Add, PrintOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { LabelAsset } from '../components/AssetForm/sections';
import { PageContainer, PageCard, SectionHeader } from '../../common/components/styled-ui-elements';
import { BackToDashboardButton } from '../../common/components/navigation';
import { AssetsGrid } from '../components/AssetList';
import { useGetCentersQuery } from '../../../services/DimensionService/dimensionApi';
import { useGetMyPreferencesQuery } from '../../../services/UserPreferencesService';
import { EnumView } from '../../../services/DimensionService/types/dimensionTypes';

// Kept local to this page rather than shared — the near-identical control on ProfilePage is its
// own local copy too, per the "don't extract until a third consumer exists" rule.
const CentersFilter: React.FC<{ centers: EnumView[]; selectedCenters: string[]; onChange: (centers: string[]) => void }> = ({
  centers, selectedCenters, onChange,
}) => {
  const nameFor = (value: string) => centers.find((c) => c.value === value)?.name || value;

  return (
    <Autocomplete
      multiple
      size="small"
      options={centers.map((c) => c.value)}
      value={selectedCenters}
      onChange={(_, newValue) => onChange(newValue)}
      getOptionLabel={nameFor}
      sx={{ minWidth: 260, maxWidth: 400 }}
      renderInput={(params) => (
        <TextField {...params} label="Centers" placeholder={selectedCenters.length === 0 ? 'All Centers' : undefined} />
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip {...getTagProps({ index })} key={option} label={nameFor(option)} size="small" />
        ))
      }
    />
  );
};

const AssetList: React.FC = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState<LabelAsset[]>([]);
  const [selectedCenters, setSelectedCenters] = useState<string[]>([]);
  const [hasSeededDefaultCenters, setHasSeededDefaultCenters] = useState(false);

  const { data: centers = [] } = useGetCentersQuery();
  const { data: preferences } = useGetMyPreferencesQuery();

  // Seed the Centers filter from the user's saved default Centers exactly once, so later
  // edits made directly on this page aren't clobbered by a background refetch.
  useEffect(() => {
    if (!hasSeededDefaultCenters && preferences) {
      setSelectedCenters(preferences.defaultCenters);
      setHasSeededDefaultCenters(true);
    }
  }, [hasSeededDefaultCenters, preferences]);

  // Also treat a manual edit as "seeded" — otherwise a preferences load that resolves after the
  // user has already changed the filter would overwrite their choice with the saved defaults.
  const handleCentersChange = (next: string[]) => {
    setSelectedCenters(next);
    setHasSeededDefaultCenters(true);
  };

  return (
    <PageContainer sx={{ backgroundColor: '#f8fafc' }}>
      <Box display="flex" justifyContent="center">
        <PageCard>
          {/* Professional Header */}
          <Box sx={{ 
            backgroundColor: '#ffffff',
            borderBottom: '2px solid #3b82f6',
            p: 2.5
          }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <SectionHeader 
                  icon={<Build sx={{ mr: 1, color: '#d97706' }} />}
                  title="Asset Management"
                />
                <Typography 
                  variant="body2" 
                  sx={{ color: '#64748b', ml: 4 }}
                >
                  Manage equipment, devices, and preventive maintenance schedules
                </Typography>
              </Box>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  startIcon={<PrintOutlined />}
                  disabled={selectedAssets.length === 0}
                  onClick={() => navigate('/assets/print-labels', { state: { assets: selectedAssets } })}
                  sx={{ fontWeight: 600, textTransform: 'none', px: 2.5 }}
                >
                  {selectedAssets.length > 0 ? `Print Labels (${selectedAssets.length})` : 'Print Labels'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => navigate('/assets/create')}
                  sx={{
                    backgroundColor: '#162346',
                    '&:hover': { backgroundColor: '#1a2a52' },
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 2.5,
                  }}
                >
                  Create Asset
                </Button>
                <BackToDashboardButton />
              </Box>
            </Box>
          </Box>
          
          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            {/* Search and Filter Section */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                size="small"
                placeholder="Search assets by name, tag, category, center..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{ flex: 1, maxWidth: 500 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#94a3b8' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <CentersFilter centers={centers} selectedCenters={selectedCenters} onChange={handleCentersChange} />
              <FormControlLabel
                control={
                  <Switch
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Show Inactive
                  </Typography>
                }
              />
            </Box>

            {/* Grid Section */}
            <AssetsGrid 
              searchText={searchText}
              showInactive={showInactive}
              selectedCenters={selectedCenters}
              onSelectionChange={setSelectedAssets}
            />
          </CardContent>
        </PageCard>
      </Box>
    </PageContainer>
  );
};

export default AssetList;
